import {ref, computed, nextTick} from "vue";

import {fetchProducts, fetchStoreProducts} from "@songfinch/data-source/products";
import {sortedProducts} from "@songfinch/utils/src/map";

import type {ProductListItem, ProductFilterType, CMSPageProductData, CMSProductSortObject, ProductFilterSelectionType} from "@songfinch/types/types";
import {removeEmptyValues} from "@songfinch/utils/src/normalizers";

import {logError} from "@songfinch/utils/src/error";

export function useProducts() {
    const cmsSortProducts = ref<CMSProductSortObject[]>([]);
    const products = ref<ProductListItem[]>([]);
    const productFilters = ref<ProductFilterType[]>([]);
    const isLoading = ref<boolean>(false);
    const filterSelections = ref<ProductFilterSelectionType>();

    const setCMSProductSort = (sort: CMSProductSortObject[]) => { 
        cmsSortProducts.value = sort;
    };

    const search = async () => {
        try {
            const res = await searchRequest();

            products.value = res || [];
            await loadingComplete();
        } catch (error) {
            logError(error);
        }
    };

    const getProductsFromCMSSortFilters = async (products: CMSPageProductData, storyId?: number) => {
        try {
            if (!products) return;
            const productSortArray = Object.values(products);
            setCMSProductSort(productSortArray);
            // CMS calls product.name as product.id
            const CMSFeaturedProductNameArray = productSortArray.map((item) => item.product.id);
            if (CMSFeaturedProductNameArray.length) await searchProductsByProductNames(CMSFeaturedProductNameArray, storyId);
        } catch (error) {
            logError(error);
        }
    };

    const searchProductsByProductNames = async (productNames: string[], storyId?: number) => {
        try {
            if (!productNames.length) return;
            const res = await fetchProducts({
                name: productNames,
                story_id: storyId
            });
    
            if (res.length) {
                products.value = res || [];
            }
    
            await loadingComplete();
        } catch (error) {
            logError(error);
        }
    };


    const loadingComplete = async () =>{
        await nextTick();
        isLoading.value = false;
    };

    const searchRequest = async () => {
        try {
            isLoading.value = true;

            return await fetchStoreProducts({
                ...filters.value,
            });
        } catch (error) {
            logError(error);
        }
    };

    const filters = computed(() => {
        if (!filterSelections.value) return {};
        return removeEmptyValues(filterSelections.value);
    });

    const filtersLength = computed(() => Object.keys(filters.value).length);

    const setFilterSelection = (filters: ProductFilterType[]) => {
        filterSelections.value = (
            filters.reduce<ProductFilterSelectionType>((acc, filter) => {
                if (!acc[filter.id]) {
                    acc[filter.id] = "";
                }
                return acc;
            }, {})
        );
    };

    const filteredAndSortedProducts = computed(() => {
        if (!cmsSortProducts.value) return products.value;
    
        const filteredProducts = products.value.filter((product) => {
            if (product?.name  !== "free-song-streaming") return product;
        });

        if (filtersLength.value) return filteredProducts;
    
        const sortOrder = cmsSortProducts.value.reduce((sortOrder: string[], product: CMSProductSortObject) => {
            if (product?.product?.id) {
                sortOrder.push(product.product.id);
            }
            return sortOrder;
        }, []);
    
        if (sortOrder.length === 0) {
            return products.value;
        }
        return sortedProducts(filteredProducts, sortOrder);
    });

    return {
        isLoading,
        products,
        productFilters,
        filteredAndSortedProducts,
        setCMSProductSort,
        search,
        searchProductsByProductNames,
        getProductsFromCMSSortFilters,
        filters,
        filterSelections,
        setFilterSelection
    };
}
